import React from 'react';
import { Spinner, Table } from 'react-bootstrap';

import Card from 'components/DesignSystem/Card';
import { formatNumberBR } from 'components/Relatorios/shared/RelatorioTable';

import useMensalSecaoParams from 'hooks/Relatorios/Mensal/useMensalSecaoParams';
import { useGetMensalResultadosQuery } from 'services/api/products/relatorios';

import './index.scss';

/**
 * "Preços" e "Energia Naturalmente Afluente" têm o mesmo formato de tabela
 * (header de 2 níveis: revisão/mês → 2 sub-colunas) já usado no Resultados do
 * Semanal — só os rótulos das revisões mudam (RV-grupos em vez de datas de
 * revisão semanal / meses em vez de revisões). Sem `armazenamento`,
 * `diretrizes` nem `consideracoes_html`: essas sub-tabelas não aparecem no
 * print do Mensal (só Preços + ENA).
 */
export default function Resultados() {
  const params = useMensalSecaoParams();

  const { data, isLoading } = useGetMensalResultadosQuery(params);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center p-3">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { precos, ena } = data;

  return (
    <div className="mensal-resultados">
      <Card title="Preços" className="relatorio-table">
        <Table responsive hover>
          <thead>
            <tr>
              <th rowSpan={2}>Subsistema</th>
              {precos.revisoes.map((rev) => <th key={rev} colSpan={2}>{rev}</th>)}
            </tr>
            <tr>
              {precos.revisoes.map((rev) => (
                <React.Fragment key={rev}>
                  <th>PLD (sem limite*)</th>
                  <th>PLD</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {precos.linhas.map((linha) => (
              <tr key={linha.subsistema}>
                <td>{linha.subsistema}</td>
                {linha.valores.map((valor, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <React.Fragment key={`${linha.subsistema}-${i}`}>
                    <td>{formatNumberBR(valor.pld_sem_limite)}</td>
                    <td>{formatNumberBR(valor.pld)}</td>
                  </React.Fragment>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <Card title="Energia Naturalmente Afluente" className="relatorio-table">
        <p className="mensal-resultados__legenda">
          Premissas - ENAs mensais
        </p>
        <Table responsive hover>
          <thead>
            <tr>
              <th rowSpan={2}>Subsistema</th>
              {ena.revisoes.map((mes) => <th key={mes} colSpan={2}>{mes}</th>)}
            </tr>
            <tr>
              {ena.revisoes.map((mes) => (
                <React.Fragment key={mes}>
                  <th>MWmed</th>
                  <th>%MLT</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {ena.linhas.map((linha) => (
              <tr key={linha.subsistema}>
                <td>{linha.subsistema}</td>
                {linha.valores.map((valor, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <React.Fragment key={`${linha.subsistema}-${i}`}>
                    <td>{formatNumberBR(valor.mw_med)}</td>
                    <td>{`${formatNumberBR(valor.pct_mlt)}%`}</td>
                  </React.Fragment>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </div>
  );
}
